// StepRanger theme — global behaviour: navigation, dialogs, predictive search, cart drawer, analytics events, lead forms.
// No dependencies. Budget: ≤ 150 KB first-party JS for the whole theme (REBUILD-PLAN §4); this file is ~12 KB.

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const routes = window.routes || {};
const desktop = window.matchMedia('(min-width: 1024px)');

/* ---------- Navigation: one markup; drawer on phones, dropdowns on desktop ---------- */
const nav = $('[data-site-nav]');
const navOpenBtn = $('[data-nav-open]');
let navReturnFocus = null;

function closeAllGroups(except) {
  $$('[data-nav-trigger]').forEach((t) => {
    if (t === except) return;
    t.setAttribute('aria-expanded', 'false');
    document.getElementById(t.getAttribute('aria-controls')).hidden = true;
  });
}
function openNav() {
  navReturnFocus = document.activeElement;
  nav.classList.add('is-open');
  document.body.classList.add('nav-open');
  navOpenBtn?.setAttribute('aria-expanded', 'true');
  $('[data-nav-close]', nav)?.focus();
}
function closeNav() {
  if (!nav?.classList.contains('is-open')) return;
  nav.classList.remove('is-open');
  document.body.classList.remove('nav-open');
  navOpenBtn?.setAttribute('aria-expanded', 'false');
  navReturnFocus?.focus();
}
if (nav) {
  navOpenBtn?.addEventListener('click', openNav);
  $('[data-nav-close]', nav)?.addEventListener('click', closeNav);
  $$('[data-nav-trigger]', nav).forEach((t) => t.addEventListener('click', () => {
    const open = t.getAttribute('aria-expanded') === 'true';
    if (desktop.matches) closeAllGroups(t);
    t.setAttribute('aria-expanded', String(!open));
    document.getElementById(t.getAttribute('aria-controls')).hidden = open;
  }));
  document.addEventListener('click', (e) => { if (desktop.matches && !e.target.closest('.nav-group')) closeAllGroups(); });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const openTrigger = $$('[data-nav-trigger][aria-expanded="true"]').pop();
    if (desktop.matches && openTrigger) { closeAllGroups(); openTrigger.focus(); } else closeNav();
  });
  desktop.addEventListener('change', () => { closeNav(); closeAllGroups(); });
}

/* ---------- Dialogs (cart, search): native <dialog> gives focus trap, Esc and an inert page ---------- */
function openDialog(dialog) {
  if (!dialog || dialog.open) return;
  closeNav(); dialog.showModal();
  // Focus the title rather than the first control (the close button), so no focus ring shows after a tap (plan 11-01 #40).
  dialog.querySelector('[data-dialog-focus]')?.focus({ preventScroll: true });
}
$$('dialog').forEach((d) => {
  d.addEventListener('click', (e) => { if (e.target === d || e.target.closest('[data-dialog-close]')) d.close(); });
});

/* ---------- Predictive search ---------- */
const searchDialog = $('[data-search-dialog]');
const searchInput = $('[data-search-input]');
const searchResults = $('[data-search-results]');
let searchTimer; let searchAbort;
$$('[data-search-open]').forEach((a) => a.addEventListener('click', (e) => {
  if (!searchDialog) return;
  e.preventDefault(); openDialog(searchDialog); searchInput?.focus();
}));
searchInput?.addEventListener('input', () => {
  clearTimeout(searchTimer);
  const q = searchInput.value.trim();
  if (q.length < 2) { searchResults.innerHTML = ''; return; }
  searchTimer = setTimeout(async () => {
    searchAbort?.abort(); searchAbort = new AbortController();
    const url = `${routes.predictive_search_url}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,page,article,collection&resources[limit]=4&resources[limit_scope]=each`;
    try {
      const html = await (await fetch(url, { signal: searchAbort.signal })).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      searchResults.innerHTML = $('#PredictiveResults', doc)?.outerHTML || '';
    } catch (err) { if (err.name !== 'AbortError') searchResults.innerHTML = ''; }
  }, 200);
});

/* ---------- Cart drawer: Cart AJAX API + Section Rendering API ---------- */
const cartDrawer = $('[data-cart-drawer]');
// Every cart body (the drawer, and the /cart page when on it) names its section; one request re-renders all of them.
const cartSections = () => $$('[data-cart-body]').map((b) => b.dataset.sectionId);
function renderCartSections(sections) {
  $$('[data-cart-body]').forEach((body) => {
    const html = sections?.[body.dataset.sectionId];
    if (!html) return;
    const fresh = $('[data-cart-body]', new DOMParser().parseFromString(html, 'text/html'));
    if (fresh) body.innerHTML = fresh.innerHTML;
  });
}
function setCount(n) {
  $$('[data-cart-count]').forEach((c) => { c.textContent = n; c.hidden = n === 0; });
  $$('[data-cart-title-count]').forEach((c) => { c.textContent = n > 0 ? ` (${n})` : ''; });
  $$('[data-cart-open]').forEach((a) => a.setAttribute('aria-label', `Cart, ${n} item${n === 1 ? '' : 's'}`));
}
function showCartError(message, scope = cartDrawer) {
  const el = $('[data-cart-error]', scope);
  if (el) { el.textContent = message; el.hidden = false; }
}
$$('[data-cart-open]').forEach((a) => a.addEventListener('click', (e) => {
  if (!cartDrawer) return; e.preventDefault(); openDialog(cartDrawer);
}));

// Add to cart: FormData from any /cart/add form, or {items} from a page script via the "cart:add" event
// (the vehicle page adds the truck, battery set and extras in one request). The drawer opens with the result.
async function addToCart(body, button) {
  button?.setAttribute('aria-busy', 'true');
  try {
    const json = !(body instanceof FormData);
    if (json) body = JSON.stringify({ ...body, sections: cartSections() }); else body.append('sections', cartSections().join(','));
    const res = await fetch(`${routes.cart_add_url}.js`, {
      method: 'POST', body,
      headers: json ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
    });
    const data = await res.json();
    if (!res.ok) { openDialog(cartDrawer); showCartError(data.description || 'That item could not be added.'); return false; }
    renderCartSections(data.sections);
    const cart = await (await fetch(`${routes.cart_url}.js`)).json();
    setCount(cart.item_count);
    // On the cart page the page itself re-renders, so the drawer stays shut (the cart add-ons, John 2026-10-31).
    if (location.pathname !== routes.cart_url) openDialog(cartDrawer);
    return true;
  } finally { button?.removeAttribute('aria-busy'); }
}
document.addEventListener('submit', async (e) => {
  const form = e.target;
  if (!cartDrawer || !(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) return;
  e.preventDefault();
  try { await addToCart(new FormData(form), form.querySelector('[type="submit"]')); } catch { form.submit(); }
});
document.addEventListener('cart:add', (e) => {
  if (!cartDrawer) return;
  addToCart({ items: e.detail.items }, e.detail.button).catch(() => showCartError('Something went wrong. Please try again.'));
});

// Quantity and remove buttons, in the drawer or on the /cart page.
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-cart-body] [data-qty]');
  if (!btn) return;
  const body = btn.closest('[data-cart-body]');
  const line = Number(btn.closest('[data-line]').dataset.line);
  btn.disabled = true;
  body.setAttribute('aria-busy', 'true');
  try {
    const res = await fetch(`${routes.cart_change_url}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity: Number(btn.dataset.qty), sections: cartSections() }),
    });
    const data = await res.json();
    if (!res.ok) { showCartError(data.description || 'That change could not be made.', body); btn.disabled = false; return; }
    renderCartSections(data.sections);
    setCount(data.item_count);
    $$('[data-cart-status]').forEach((s) => { s.textContent = 'Cart updated.'; });
  } catch { showCartError('Something went wrong. Please try again.', body); btn.disabled = false; }
  finally { body.removeAttribute('aria-busy'); }
});

/* ---------- Below-the-fold images: [data-lazy] imgs get their src only when within 400 px of the viewport ----------
   (loading=lazy alone lets Chrome start them before first paint on fast links, which competes with the LCP image). */
const lazyIO = 'IntersectionObserver' in window && new IntersectionObserver((entries) => entries.forEach((e) => {
  if (!e.isIntersecting) return;
  const img = e.target;
  if (img.dataset.srcset) img.srcset = img.dataset.srcset;
  if (img.dataset.src) img.src = img.dataset.src;
  img.removeAttribute('data-lazy'); lazyIO.unobserve(img);
}), { rootMargin: '400px 0px' });
$$('img[data-lazy]').forEach((img) => (lazyIO ? lazyIO.observe(img) : (img.src = img.dataset.src)));
/* [data-after-load] imgs (M12: the home lineup photos, which sit just under the hero on phones) start only after the load
   event, so on a slow connection they never share bandwidth with the hero image (home LCP +0.7 s otherwise). */
const afterLoadImgs = () => $$('img[data-after-load]').forEach((img) => {
  // <picture> sources first (snippets/picture-asset), so the browser picks AVIF/WebP before the JPEG src lands.
  $$('source[data-srcset]', img.closest('picture') || img).forEach((s) => { s.srcset = s.dataset.srcset; s.removeAttribute('data-srcset'); });
  if (img.dataset.srcset) img.srcset = img.dataset.srcset; if (img.dataset.src) img.src = img.dataset.src; img.removeAttribute('data-after-load');
});
window.srAfterLoadImgs = afterLoadImgs;
document.readyState === 'complete' ? afterLoadImgs() : addEventListener('load', afterLoadImgs, { once: true });

/* ---------- Product recommendations: fetched (Section Rendering API) when the section scrolls near ---------- */
document.querySelectorAll('[data-recommendations]').forEach((el) => {
  const io = new IntersectionObserver(async ([e]) => {
    if (!e.isIntersecting) return;
    io.disconnect();
    try {
      const html = await (await fetch(el.dataset.recommendations)).text();
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
      if (fresh && fresh.querySelector('li')) { el.innerHTML = fresh.innerHTML; el.hidden = false; }
    } catch { /* recommendations are optional */ }
  }, { rootMargin: '600px 0px' });
  io.observe(el);
});

/* ---------- Analytics events (analysis/09 §3 — GA4 records none of these today) ----------
   Published as Shopify customer events (a custom pixel forwards them to GA4) and to gtag when a tag is on the page.
   tel_click: every tel:/sms: link. contact_click: email links marked [data-contact-click]. generate_lead: a form's
   success state ([data-lead-success], rendered only after Shopify accepted the post). */
const track = (name, params) => {
  window.Shopify?.analytics?.publish?.(name, params);
  if (typeof window.gtag === 'function') window.gtag('event', name, params);
};
document.addEventListener('click', (e) => {
  const a = e.target.closest('a[href^="tel:"], a[href^="sms:"], [data-contact-click]');
  if (!a) return;
  const href = a.getAttribute('href') || '';
  const placement = a.closest('[data-placement]')?.dataset.placement || a.closest('header, footer, dialog, main')?.tagName.toLowerCase() || '';
  if (/^(tel|sms):/.test(href)) track('tel_click', { method: href.startsWith('sms:') ? 'text' : 'phone', placement, page: location.pathname });
  else track('contact_click', { method: a.dataset.contactClick, placement, page: location.pathname });
});

/* ---------- Lead forms: page, first-touch UTM and landing page go with every message (analysis/09 §2) ---------- */
const attribution = (() => {
  const q = new URLSearchParams(location.search);
  const utm = ['source', 'medium', 'campaign', 'term', 'content'].map((k) => [k, q.get(`utm_${k}`)]).filter(([, v]) => v);
  let first = null;
  try { first = JSON.parse(localStorage.getItem('sr_first_touch') || 'null'); } catch { /* storage blocked */ }
  if (!first) {
    first = { utm: Object.fromEntries(utm), landing: location.pathname, referrer: document.referrer ? new URL(document.referrer).hostname : '' };
    try { localStorage.setItem('sr_first_touch', JSON.stringify(first)); } catch { /* storage blocked */ }
  }
  const pairs = Object.entries(first.utm || {});
  return { utm: pairs.length ? pairs.map(([k, v]) => `${k}=${v}`).join(' · ') : '(none)', landing: first.landing + (first.referrer ? ` (from ${first.referrer})` : '') };
})();
$$('[data-lead-form]').forEach((f) => {
  const set = (name, v) => { const i = f.querySelector(`input[name="contact[${name}]"]`); if (i) i.value = v; };
  set('Page', location.origin + location.pathname);
  set('UTM', attribution.utm);
  set('Landing page', attribution.landing);
  // A form whose type the visitor picks (press or creator) remembers it for the success event after the reload.
  f.addEventListener('submit', () => {
    const t = f.querySelector('[name="contact[Form type]"]:checked, input[type="hidden"][name="contact[Form type]"]')?.value;
    try { if (t) sessionStorage.setItem(`sr_lead_type_${f.id}`, t); } catch { /* storage blocked */ }
  });
});
// Once per submission: a reload of the success page doesn't count again.
$$('[data-lead-success]').forEach((el) => {
  const key = `sr_lead_${el.dataset.leadSuccess}_${location.pathname}`;
  try { if (sessionStorage.getItem(key) === location.href) return; sessionStorage.setItem(key, location.href); } catch { /* storage blocked */ }
  let type = el.dataset.leadSuccess;
  try { type = sessionStorage.getItem(`sr_lead_type_${el.closest('form')?.id}`) || type; } catch { /* storage blocked */ }
  track('generate_lead', { form_type: type, page: location.pathname });
});

/* ---------- "My truck" (plan 11-01 #37): the owner's model key in localStorage, so Support, Parts and Accessories open on
   the right truck. Set by a model support page ([data-truck-set]) or any [data-truck-pick] click (hub tiles, parts tiles,
   fits chips); cleared by [data-truck-clear]. [data-truck-line] shows "Your truck: <name>" from the matching pick element. */
const truck = {
  get() { try { return localStorage.getItem('sr_truck') || ''; } catch { return ''; } },
  set(k) {
    try { if (k) localStorage.setItem('sr_truck', k); else localStorage.removeItem('sr_truck'); } catch { /* storage blocked */ }
    document.dispatchEvent(new CustomEvent('truck:change', { detail: k || '' }));
  },
};
window.srTruck = truck;
const truckSet = $('[data-truck-set]');
if (truckSet?.dataset.truckSet) truck.set(truckSet.dataset.truckSet);
document.addEventListener('click', (e) => {
  const pick = e.target.closest('[data-truck-pick]');
  if (pick) truck.set(pick.dataset.truckPick);
  if (e.target.closest('[data-truck-clear]')) truck.set('');
});
function renderTruck() {
  const k = truck.get();
  const pick = k ? $(`[data-truck-pick="${CSS.escape(k)}"]`) : null;
  $$('[data-truck-pick]').forEach((p) => p.toggleAttribute('data-truck-current', p === pick));
  $$('[data-truck-line]').forEach((el) => {
    el.hidden = !pick;
    if (!pick) return;
    const name = el.querySelector('[data-truck-name]');
    if (name) name.textContent = pick.dataset.truckName || pick.textContent.trim();
    const link = el.querySelector('a[data-truck-link]');
    if (link) link.href = pick.dataset.truckHref || pick.getAttribute('href') || '#';
  });
  $$('select[data-truck-select]').forEach((sel) => {
    if (!pick || sel.dataset.truckPicked) return;
    const name = pick.dataset.truckName || pick.textContent.trim();
    const opt = Array.from(sel.options).find((o) => o.textContent.trim() === name);
    if (opt) { sel.value = opt.value; sel.dataset.truckPicked = '1'; }
  });
}
renderTruck();
document.addEventListener('truck:change', renderTruck);
